import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f6cb7312"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "datebox-container" }

import { FieldSize } from "@/enums/system/FieldSize";
import { FieldValidation } from "acquaint-api/validation/FieldValidation";
import { PropType, computed, ref } from "vue";
import { DxDateBox } from "devextreme-vue/date-box";
import { ValidatedEvent } from "devextreme/ui/validator";
import { validationCallback } from "@/utils/validation/FieldValidator";
import { DxValidator } from "devextreme-vue";
import { DxCustomRule, DxRequiredRule } from "devextreme-vue/validator";
import { formatCurrentDate } from "bright-logic-core/src/format";
import moment from "moment";
import { LabelPosition } from "@/enums/LabelPosition";


export default /*#__PURE__*/_defineComponent({
  __name: 'acquaint-date-box',
  props: {
  /**
   * The string value used with `v-model` to control the visibility or state of a modal or component.
   * @type {string}
   * @required: false
   * @default: null
   */
  modelValue: {
    type: String,
    required: false,
    default: null,
  },
  /**
   * The validation rules applied to the field.
   * @type {FieldValidation}
   * @required
   */
  fieldValidation: {
    type: Object as PropType<FieldValidation>,
    required: true,
  },
  /**
   * Determines whether the specific control is read-only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * The label displayed for the control.
   * @type {string}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * The width of the field, defined by the `FieldSize` enum.
   * @type {FieldSize}
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },
  /**
   * Custom validation function for the field.
   * @type {Function}
   * @optional
   */
  customValidation: {
    type: Function,
  },
  /**
   * The message displayed when validation fails.
   * @type {string}
   * @optional
   */
  invalidMessage: {
    type: String,
  },
  /**
   * The maximum allowed date.
   * @type {Date}
   * @default 50 years from the current date
   */
  maxDate: {
    type: Date,
    default: () => moment().add(50, "years").toDate(),
  },
  /**
   * The minimum allowed date.
   * @type {Date}
   * @default '1900-01-01'
   */
  minDate: {
    type: Date,
    default: () => new Date("1900-01-01"),
  },
  /**
   * Determines whether the item should be disabled.
   * @type {boolean}
   * @default false
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the time should be included in the date input.
   * @type {boolean}
   * @default false
   */
  includeTime: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether the input field is required.
   * @type {boolean}
   * @default false
   */
  requiredInput: {
    type: Boolean,
    default: false,
  },
  /**
   * The validation group name used for form validation.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    default: "",
  },
  /**
   * Determines whether to show a clear button.
   * @type {boolean}
   * @default false
   */
  showClearButton: {
    type: Boolean,
    default: false,
  },
  /**
   * Dates that should be highlighted.
   * @type {Date[]}
   * @default []
   */
  highlightedDates: {
    type: Array as PropType<Date[][]>,
    default: () => [],
  },
  /**
   * Determines whether the input is currently valid.
   * @type {boolean}
   * @default true
   */
  isValid: {
    type: Boolean,
    default: true,
  },
  /**
   * Controls the position of the label relative to the input field.
   * 'top' (default): label above input.
   * 'left': label side-by-side with input.
   * @type {LabelPosition}
   * @default LabelPosition.Top
   */
  labelPosition: {
    type: String as PropType<LabelPosition>,
    default: LabelPosition.Top,
  },
},
  emits: [
  /**
   * Emitted when the internal date value changes, passing a formatted string (ISO or custom).
   * Typically used with v-model.
   * @event update:modelValue
   * @type {string}
   */
  "update:modelValue",
  /**
   * Emitted when the user selects or clears a date via the UI (e.g., onValueChanged).
   * Useful for listening to raw DevExtreme change events.
   * @event valueSelected
   * @type {object}
   */
  "valueSelected",
],
  setup(__props, { expose: __expose, emit }) {

const props = __props;





const dateValue = computed<string | null>({
  get: () => (props.modelValue == "" ? null : props.modelValue),
  set: (value: string | null) =>
    emit("update:modelValue", value == "" ? null : value),
});

const onValueChanged = (e: any) => {
  if (e.value === null && props.showClearButton) {
    dateValue.value = moment("01-01-1900").format("yyyy/MM/DD");
  } else {
    dateValue.value = e.value;
  }
  emit("valueSelected", e);
};

/** Custom validation configuration to override DevExpress default validation method */
const adapterConfig = {
  getValue: () => {
    return dateValue.value;
  },
  applyValidationResults: (e: ValidatedEvent) => {
    isValid.value = !!e.isValid;

    if (!props.isValid) {
      isValid.value = props.isValid;
    }

    // Construct list of validation errors to be displayed to the user when they hover over the field
    validationErrors.value =
      !e.isValid && e.brokenRule?.message ? e.brokenRule.message : "";
  },
  validationRequestsCallbacks: [],
};

const dateBoxType = computed(() => (props.includeTime ? "datetime" : "date"));
const validationErrors = ref("");
const isValid = ref(true);
const opened = ref(false);

/** Handles custom max date validation */
const maxDateValidation = () => {
  // Allow undefined/null dates
  if (!dateValue.value) {
    return true;
  }

  moment.locale("en-GB");
  return props.maxDate
    ? moment(dateValue.value).isSameOrBefore(moment(props.maxDate))
    : true;
};

/** Handles custom min date validation */
const minDateValidation = () => {
  // Allow undefined/null dates
  if (!dateValue.value) {
    return props.minDate.toUTCString() === new Date("1900-01-01").toUTCString();
  }

  moment.locale("en-GB");
  return props.minDate
    ? moment(dateValue.value).isSameOrAfter(moment(props.minDate))
    : true;
};

const displayFormat = computed(() =>
  props.includeTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy",
);

const dateTimeDropDownOptions = {
  onHiding: () => {
    let button: HTMLElement = document.querySelector(
      ".dx-popup-done.dx-button",
    ) as HTMLElement;
    if (!button) {
      return;
    }
    button.click();
  },
};

const dateOnlyDropDownOptions = {
  toolbarItems: [
    {
      widget: "dxButton",
      toolbar: "bottom",
      location: "before",
      options: {
        text: "Today",
        onClick: () => onTodayClick(),
      },
    },
  ],
};

const dropDownOptions = computed(() =>
  props.includeTime ? dateTimeDropDownOptions : dateOnlyDropDownOptions,
);

const onTodayClick = () => {
  dateValue.value = formatCurrentDate();

  // To allow users to select time, only close if the component is in date mode
  if (!props.includeTime) {
    opened.value = false;
  }
};

const requiredMessage = computed(() =>
  props.label ? `${props.label} is required` : "This field is required",
);

const serializationFormat = computed(() =>
  props.includeTime ? "yyyy-MM-ddTHH:mm" : "yyyy-MM-dd",
);

// Exclude the today button if the max date is in the past or the min date is in the future
const excludeTodayButton = computed(() => {
  const todayStart = moment().startOf("day");
  return (
    moment(props.maxDate).startOf("day").isSameOrBefore(todayStart) ||
    moment(props.minDate).startOf("day").isAfter(todayStart)
  );
});

// This is not the optimal method to remove the Today button, but is a workaround until Devextreme adds a removal method
const onOpened = (e: any) => {
  if (
    excludeTodayButton.value &&
    (e.component._popup.option("toolbarItems").length === 3 ||
      e.component._popup.option("toolbarItems").length === 1)
  ) {
    e.component._popup.option(
      "toolbarItems",
      e.component._popup.option("toolbarItems").slice(1),
    );
  }

  if (screen.width > 1300) {
    return;
  }

  // To stop the top of the calendar being cut off on small devices, reposition it to the center of the screen
  e.component._popup.option("position", {
    my: "center",
    at: "center",
    of: window,
  });
};

// For small devices, don't set focus on input field when clicking on calendar button
const focusStateEnabled = computed(() => screen.width > 599.99);

// To overcome focus issues on iOS devices, the date box should be set to read only whilst the calendar is displayed
const dateBoxReadonly = computed(() => props.readOnly || opened.value);

// Expose the method validating the field as binding data was nonresponsive and persumably overwritten by DX inbuilts
const setValid = (value: boolean) => {
  isValid.value = value;
};
__expose({ setValid });

const calendarOptions = computed(() => ({
  cellTemplate: props.highlightedDates.length > 0 ? "custom" : "normal",
}));

const highlightMap = computed(() => {
  const map: Record<string, string> = {};

  for (const seq of props.highlightedDates) {
    if (seq.length === 0) continue;

    if (seq.length === 1) {
      const d = seq[0];
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      map[key] = "highlighted-date";
      continue;
    }

    // Multi-day sequence
    seq.forEach((d, i) => {
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (i === 0) map[key] = "highlighted-date start-highlight";
      else if (i === seq.length - 1)
        map[key] = "highlighted-date end-highlight";
      else map[key] = "highlighted-date middle-highlight";
    });
  }

  return map;
});

// Returns a CSS class for each calendar cell
function getCellCssClass({ date, view }: { date: Date; view: string }) {
  if (view !== "month" || !date) return "";

  const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  return highlightMap.value[key] || "";
}

const onFocusIn = () => {
  opened.value = true;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass(["datebox", { 'label-left': props.labelPosition === 'left' }]),
    style: _normalizeStyle({ width: props.width })
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["label", {
        asteriskVisible:
          props.requiredInput || props.fieldValidation.isRequired,
      }])
        }, _toDisplayString(__props.label), 3))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(), _createBlock(_unref(DxDateBox), {
        key: (props.includeTime ? 'with-time' : 'no-time') + props.modelValue,
        "display-format": displayFormat.value,
        value: dateValue.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((dateValue).value = $event)),
        width: __props.width,
        readOnly: dateBoxReadonly.value,
        hint: validationErrors.value,
        min: __props.minDate,
        max: __props.maxDate,
        isValid: isValid.value,
        "onUpdate:isValid": _cache[1] || (_cache[1] = ($event: any) => ((isValid).value = $event)),
        disabled: __props.disabled,
        "date-out-of-range-message": "",
        "use-mask-behavior": true,
        type: dateBoxType.value,
        dropDownOptions: dropDownOptions.value,
        "picker-type": "calendar",
        "validation-message-mode": "always",
        opened: opened.value,
        "onUpdate:opened": _cache[2] || (_cache[2] = ($event: any) => ((opened).value = $event)),
        dateSerializationFormat: serializationFormat.value,
        placeholder: "DD/MM/YYYY",
        adaptivityEnabled: true,
        showClearButton: props.showClearButton,
        "focus-state-enabled": focusStateEnabled.value,
        "calendar-options": calendarOptions.value,
        openOnFieldClick: true,
        onOpened: onOpened,
        onValueChanged: onValueChanged,
        onFocusIn: onFocusIn
      }, {
        custom: _withCtx(({ data: cell }) => [
          _createElementVNode("span", {
            class: _normalizeClass(getCellCssClass(cell))
          }, _toDisplayString(cell.text), 3)
        ]),
        normal: _withCtx(({ data: cell }) => [
          _createElementVNode("span", null, _toDisplayString(cell.text), 1)
        ]),
        default: _withCtx(() => [
          _createVNode(_unref(DxValidator), {
            adapter: adapterConfig,
            "validation-group": props.validationGroup
          }, {
            default: _withCtx(() => [
              (
              (props.fieldValidation.isRequired ||
                props.requiredInput == true) &&
              !props.disabled
            )
                ? (_openBlock(), _createBlock(_unref(DxRequiredRule), {
                    key: 0,
                    message: requiredMessage.value
                  }, null, 8, ["message"]))
                : _createCommentVNode("", true),
              (props.customValidation)
                ? (_openBlock(), _createBlock(_unref(DxCustomRule), {
                    key: 1,
                    reevaluate: true,
                    "validation-callback": __props.customValidation,
                    message: props.invalidMessage
                  }, null, 8, ["validation-callback", "message"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(DxCustomRule), {
                reevaluate: true,
                validationCallback: minDateValidation
              }),
              _createVNode(_unref(DxCustomRule), {
                reevaluate: true,
                validationCallback: maxDateValidation
              }),
              _createVNode(_unref(DxCustomRule), {
                fieldValidation: __props.fieldValidation,
                "validation-callback": _unref(validationCallback),
                reevaluate: true
              }, null, 8, ["fieldValidation", "validation-callback"])
            ]),
            _: 1
          }, 8, ["validation-group"])
        ]),
        _: 1
      }, 8, ["display-format", "value", "width", "readOnly", "hint", "min", "max", "isValid", "disabled", "type", "dropDownOptions", "opened", "dateSerializationFormat", "showClearButton", "focus-state-enabled", "calendar-options"]))
    ])
  ], 6))
}
}

})